import { z } from 'zod';

// ---------------------------------------------------------------------------
// Constantes de transporte del realm staff (contracts/staff-api.md; research R-3, R-4)
// ---------------------------------------------------------------------------

/**
 * Cabecera de firma del dispositivo:
 * `X-Fermin-Device: v=1; id=dev_…; ts=<unix_ms>; nonce=<b64url 12B>; sig=<b64url ECDSA-SHA256(canon)>`
 * `canon = METHOD + "\n" + PATH + "\n" + ts + "\n" + nonce + "\n" + hex(sha256(body || ""))`.
 * `id` se OMITE en `enroll` y `claim` (el servidor deriva el `device_id` de la clave pública) y es
 * obligatorio en las rutas autenticadas.
 */
export const DEVICE_HEADER_NAME = 'X-Fermin-Device';

/** Valor de `X-Requested-With` en toda mutación del realm staff (anti-CSRF). */
export const STAFF_CSRF_HEADER_VALUE = 'fermin-staff';

/** Cookie opaca de sesión del trabajador (research R-4). */
export const STAFF_SESSION_COOKIE_NAME = 'fermin_staff_session';

// ---------------------------------------------------------------------------
// Enums (data-model.md · worker_devices)
// ---------------------------------------------------------------------------

export const DevicePlatform = z.enum(['ios', 'android', 'desktop', 'other']);
export type DevicePlatform = z.infer<typeof DevicePlatform>;

export const DisplayMode = z.enum(['standalone', 'browser']);
export type DisplayMode = z.infer<typeof DisplayMode>;

export const DeviceStatus = z.enum(['ACTIVE', 'REVOKED']);
export type DeviceStatus = z.infer<typeof DeviceStatus>;

// ---------------------------------------------------------------------------
// Clave pública del dispositivo
// ---------------------------------------------------------------------------

export const USER_AGENT_MAX_LENGTH = 512;
export const UserAgentSchema = z.string().max(USER_AGENT_MAX_LENGTH);

/** Coordenada P-256 en base64url sin padding: 32 bytes → exactamente 43 caracteres. */
const P256CoordinateSchema = z.string().regex(/^[A-Za-z0-9_-]{43}$/, {
  message: 'Coordenada P-256 inválida: base64url de 32 bytes sin padding',
});

/**
 * JWK público ECDSA P-256 estricto: solo `kty`, `crv`, `x`, `y` (data-model.md · worker_devices).
 * Cualquier otro miembro (`d`, `alg`, `key_ops`, `ext`, …) se rechaza.
 */
export const PublicKeyJwkSchema = z
  .object({
    kty: z.literal('EC'),
    crv: z.literal('P-256'),
    x: P256CoordinateSchema,
    y: P256CoordinateSchema,
  })
  .strict();
export type PublicKeyJwk = z.infer<typeof PublicKeyJwkSchema>;

// ---------------------------------------------------------------------------
// User-Agent Client Hints (005 · QA-5)
// ---------------------------------------------------------------------------

/**
 * `navigator.userAgentData.getHighEntropyValues(['model', 'platformVersion'])`. Solo Chromium los
 * da (Safari no revela el modelo del iPhone); ambos opcionales y el registro nunca espera por
 * ellos. En Android son la única fuente del modelo y de la versión real del sistema: el user-agent
 * reducido de Chrome dice siempre `Android 10; K`.
 */
export const DeviceClientHintsSchema = z.object({
  model: z.string().trim().max(64).optional(),
  platform_version: z.string().trim().max(32).optional(),
});
export type DeviceClientHints = z.infer<typeof DeviceClientHintsSchema>;

// ---------------------------------------------------------------------------
// Enrolamiento (`POST /activation/{token}/enroll`)
// ---------------------------------------------------------------------------

/**
 * Cuerpo de enrolamiento. El cliente NO envía `device_id`: el servidor lo calcula como
 * `dev_` + crockford32(trunc128(SHA-256(SPKI))) (research R-2).
 */
export const DeviceEnrollBodySchema = z.object({
  public_key_jwk: PublicKeyJwkSchema,
  platform: DevicePlatform,
  display_mode: DisplayMode,
  user_agent: UserAgentSchema,
  /** 005 · QA-5: User-Agent Client Hints de alta entropía, si el navegador los da (Chromium). */
  client_hints: DeviceClientHintsSchema.optional(),
});
export type DeviceEnrollBody = z.infer<typeof DeviceEnrollBodySchema>;
